/*
 * Confidential and Proprietary.
 * Do not distribute without Bloom Harbor Gifts consent.
 * Copyright Bloom Harbor Gifts 2019. All rights reserved.
 */

import React, { useEffect } from 'react';
import {
    object, func, string,
    bool,
} from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import useSalesforcePersonalizedContentQuery from '../../../../../../helpers/Personalization/useSalesforcePersonalizedContentQuery';
import { getBrandName } from '../../../../../../../state/ducks/App/ducks/Brand/Brand-Selectors';
import GenericSkeleton from '../../../../GraphqlSkeletonComponents/GenericSkeleton';
import { trackEvent } from '../../../../../../../state/ducks/TagManager/ducks/TagManager/TagManager-Actions';
import useClickStreamCustomEventsTracking from '../../../../../../helpers/Personalization/useClickstreamCustomEventsTracking';
import { getCurrentPageType } from '../../../../../../../state/ducks/TagManager/ducks/TagManager/TagManager-Selectors';
import { getFeatureFlag } from '../../../../../../../state/ducks/App/ducks/Config/Config-Selectors';
import GiftFinderEdsLogin from './GiftFinderEds/GiftFinderEdsLogin';
import GiftFinderEds from '../GiftFinderEds/GiftFinderEds';

const GiftFinderAbTestLoginPrompt = ({
    data, handleSubmitEds, userSubmittedZip, isMobile, handleChange, location,
    onSubmitCheck, zip, validatedZip, setSelectedOccasion, buttonStyle,
}) => {
    const brandName = useSelector(getBrandName);
    const isGiftFinderLoginPromptEnabled = useSelector(getFeatureFlag('is-gift-finder-login-prompt-ab-test-enabled'));
    const dispatch = useDispatch();
    const pageType = useSelector(getCurrentPageType);
    const { data: loginPromptData, loading, variables } = useSalesforcePersonalizedContentQuery({
        queryName: 'giftFinderLoginPrompt',
        interactionName: `${brandName} - Get Campaign - Gift Finder Login Prompt`,
        featureFlag: true,
        optInHoldoutParticipation: isGiftFinderLoginPromptEnabled,
        resolveIdentity: false,
    });

    const giftFinderLoginGroup = loginPromptData?.contentSF?.campaign?.campaignResponses?.[0]?.payload?.userGroup;
    const giftFinderLoginEnabled = giftFinderLoginGroup?.toLowerCase?.() === 'test';

    useClickStreamCustomEventsTracking({
        salesforceResponse: loginPromptData?.contentSF,
        isFireImpression: true,
        page: { type: pageType },
        eventSubType: 'campaign-tracking',
        eventType: 'custom',
    });
    useEffect(() => {
        if (typeof giftFinderLoginGroup !== 'undefined' && !window.wuLoginPrompteGiftFinderEventFired && giftFinderLoginGroup) {
            // GA tracking
            const eventParam = {
                eventCategory: 'Experiment',
                eventAction: 'Gift Finder Login Prompt',
                eventLabel: giftFinderLoginEnabled ? 'Variant' : 'Control',
                feature_detail: giftFinderLoginEnabled ? 'Variant' : 'Control',
                feature_element: 'Gift Finder Login Prompt',
                feature_category: 'Experiment',
            };
            dispatch(trackEvent(eventParam));
            window.wuLoginPrompteGiftFinderEventFired = true;
        }
        return () => {
            window.wuLoginPrompteGiftFinderEventFired = false;
        };
    }, [giftFinderLoginGroup]);

    if (variables?.skip || loading) {
        return (
            <GenericSkeleton height="112px" maxWidth="1400px" />
        );
    }

    return (giftFinderLoginEnabled
        ? (
            <GiftFinderEdsLogin
                data={data}
                handleSubmitEds={handleSubmitEds}
                userSubmittedZip={userSubmittedZip}
                handleChange={handleChange}
                onSubmitCheck={onSubmitCheck}
                zip={zip}
                validatedZip={validatedZip}
                setSelectedOccasion={setSelectedOccasion}
                buttonStyle={buttonStyle}
                giftFinderLoginEnabled={giftFinderLoginEnabled}
            />
        )
        : (
            <GiftFinderEds
                data={data}
                handleSubmitEds={handleSubmitEds}
                userSubmittedZip={userSubmittedZip}
                handleChange={handleChange}
                onSubmitCheck={onSubmitCheck}
                zip={zip}
                validatedZip={validatedZip}
                setSelectedOccasion={setSelectedOccasion}
                buttonStyle={buttonStyle}
            />
        ));
};

GiftFinderAbTestLoginPrompt.propTypes = {
    buttonStyle: object,
    data: object.isRequired,
    onSubmitCheck: func.isRequired,
    handleChange: func.isRequired,
    handleSubmitEds: func.isRequired,
    validatedZip: string.isRequired,
    setSelectedOccasion: func.isRequired,
    userSubmittedZip: string.isRequired,
    zip: string,
    isMobile: bool.isRequired,
    location: object.isRequired,
};

GiftFinderAbTestLoginPrompt.defaultProps = {
    zip: '',
    buttonStyle: {},
};

export default GiftFinderAbTestLoginPrompt;
